import { UserRoleToTypeName, UserTypeDBFormat, UserTypeName } from '../../constants/securspace-constants';

/**
 * Shape of a user row as returned by the paginated account-users endpoint.
 */
export interface ManagedUser {
  username: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  isOptedIntoText?: boolean;
  smsVerified?: boolean | null;
  isLocationManager?: boolean;
  locationIds?: string[];
  accountId?: string;
}

export interface RoleChipColors {
  bg: string;
  color: string;
}

/**
 * Chip tint per role display name. The design handoff specified Account Owner, Administrator,
 * and Dispatcher; Gate Manager (teal) and Gate Clerk (amber) are assigned distinct hues so no
 * two roles share a color.
 */
const ROLE_CHIP_COLORS: Record<string, RoleChipColors> = {
  [UserTypeName.ACCOUNT_OWNER]: { bg: 'rgba(63,81,181,0.10)', color: '#3F51B5' },
  [UserTypeName.ADMIN]: { bg: '#EEF0F3', color: '#324559' },
  [UserTypeName.DISPATCHER]: { bg: 'rgba(77,170,225,0.14)', color: '#1E86C4' },
  [UserTypeName.GATE_MANAGER]: { bg: 'rgba(0,150,136,0.12)', color: '#00796B' },
  [UserTypeName.GATE_CLERK]: { bg: 'rgba(245,124,0,0.14)', color: '#B25400' },
};

const DEFAULT_ROLE_CHIP: RoleChipColors = { bg: '#F5F5F5', color: '#5C6771' };

/** Human-readable role label (e.g. ROLE_USERTYPE_OWNER → "Administrator"). */
export const getRoleDisplayName = (role?: string): string =>
  (role && UserRoleToTypeName[role as keyof typeof UserRoleToTypeName]) || '';

/** Chip background/foreground colors for a DB role value. */
export const getRoleChipColors = (role?: string): RoleChipColors =>
  ROLE_CHIP_COLORS[getRoleDisplayName(role)] || DEFAULT_ROLE_CHIP;

/** Two-letter initials from name, falling back to the username's first character. */
export const getUserInitials = (user: ManagedUser): string => {
  const first = (user.firstName || '').trim();
  const last = (user.lastName || '').trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || first.charAt(0).toUpperCase();
  }
  return (user.username || '?').charAt(0).toUpperCase();
};

/** Full display name, falling back to an empty string when both parts are missing. */
export const getUserFullName = (user: ManagedUser): string =>
  `${user.firstName || ''} ${user.lastName || ''}`.trim();

export type SmsStateVariant = 'enabled' | 'error' | 'disabled';

export interface SmsState {
  label: string;
  variant: SmsStateVariant;
}

/**
 * Derives the SMS indicator state (label + variant) for a user row.
 *
 * Three business states (see the SMS column in the Users table):
 * - Gray  (`disabled`): not enrolled — the user has not consented to text messages, either
 *                       because they never opted in or because they opted out (e.g. replied STOP).
 * - Red   (`error`)   : consented, but the number is known undeliverable (`smsVerified === false`,
 *                       an explicit Twilio Lookup rejection). Consent is deliberately preserved on
 *                       the backend when verification fails, which is what makes this state
 *                       reachable — a delivery problem is not a withdrawal of consent.
 * - Green (`enabled`) : consented and believed deliverable.
 *
 * `smsVerified` is null only for users who never opted in, so the final fallthrough is a
 * defensive default rather than a state the API is expected to produce.
 */
export const getSmsState = (user: ManagedUser): SmsState => {
  if (!user.isOptedIntoText) {
    return { label: 'Not opted into SMS', variant: 'disabled' };
  }
  if (user.smsVerified === false) {
    return { label: 'SMS Not Verified', variant: 'error' };
  }
  if (user.smsVerified === true) {
    return { label: 'SMS Verified', variant: 'enabled' };
  }
  return { label: 'Opted into SMS', variant: 'enabled' };
};

export interface SortOption {
  value: string;
  label: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
}

/** Sort dropdown options, mapping UI selections to backend sortBy/sortDir params. */
export const USER_SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc', label: 'Name A–Z', sortBy: 'NAME', sortDir: 'asc' },
  { value: 'name-desc', label: 'Name Z–A', sortBy: 'NAME', sortDir: 'desc' },
  { value: 'email-asc', label: 'Email A–Z', sortBy: 'USERNAME', sortDir: 'asc' },
  { value: 'role-asc', label: 'Role', sortBy: 'ROLE', sortDir: 'asc' },
];

export const DEFAULT_USER_SORT_VALUE = 'name-asc';

export interface RoleFilterOption {
  /** Stable select value / display label. */
  value: string;
  /** DB role values this option maps to (empty for "All"). */
  roles: string[];
}

const ALL_ROLES_OPTION: RoleFilterOption = { value: 'All roles', roles: [] };

/**
 * Role filter options for the toolbar, scoped to the account type.
 * "Administrator" intentionally maps to both OWNER and ADMIN DB roles, which both
 * render as "Administrator" in the UI.
 */
export const getRoleFilterOptions = (isBuyer: boolean): RoleFilterOption[] => {
  const options: RoleFilterOption[] = [
    ALL_ROLES_OPTION,
    { value: UserTypeName.ACCOUNT_OWNER, roles: [UserTypeDBFormat.ROLE_USERTYPE_PRIMARY_OWNER] },
    {
      value: UserTypeName.ADMIN,
      roles: [UserTypeDBFormat.ROLE_USERTYPE_OWNER, UserTypeDBFormat.ROLE_USERTYPE_ADMIN],
    },
  ];

  if (isBuyer) {
    options.push({ value: UserTypeName.DISPATCHER, roles: [UserTypeDBFormat.ROLE_USERTYPE_DISPATCHER] });
  } else {
    options.push(
      { value: UserTypeName.GATE_MANAGER, roles: [UserTypeDBFormat.ROLE_USERTYPE_GATEMANAGER] },
      { value: UserTypeName.GATE_CLERK, roles: [UserTypeDBFormat.ROLE_USERTYPE_GATECLERK] },
    );
  }

  return options;
};
